/**
 * Form Model Map Utility
 * 
 * Compile-time utility for accessing form model metadata and providing direct model
 * instantiation capabilities. This utility serves as a static mapping layer that
 * allows components to access form models without dependency injection, which is
 * necessary for certain scenarios like form rendering where services may not be
 * available at compile time.
 * 
 * Key Functions:
 * - getFormModel: Retrieves the model class for a given form type
 * - getFormTitle: Gets the human-readable title for a form type
 * - getFormFieldLabel: Gets the display label for a specific form field
 * 

 * 
 * Primary Use Cases:
 * - Form view component initialization and rendering

 * 
 * Components that utilize these functions:
 * - FormViewComponent: For initializing form models during view rendering
 * 
 * Note: This utility differs from FormModelService in that it provides
 * compile-time access to form models, while the service provides runtime
 * metadata access. This is necessary for form rendering scenarios where
 * services may not be available or when direct model instantiation is needed.
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { WeeklySiteInspection } from '../models/forms/weekly-site-inspection/weekly-site-inspection.model';
import { NearMissReport } from '../models/forms/near-miss-report/near-miss-report.model';
import { HotWorkPermit } from '../models/forms/hot-work-permit/hot-work-permit.model';
import { FallProtectionPlan } from '../models/forms/fall-protection-plan/fall-protection-plan.model';
import { PreTaskSafetyPlan } from '../models/forms/pre-task-safety-plan/pre-task-safety-plan.model';
import { PfizerPreTaskSafetyPlan } from '../models/forms/pfizer-pre-task-safety-plan/pfizer-pre-task-safety-plan.model';
import { ConfinedSpaceAlternateEntryForm } from '../models/forms/confined-space-alt-entry/confined-space-alt-entry.model';
import { AerialAndScissorLift} from '../models/forms/aerial-and-scissor-lift/aerial-and-scissor-lift.model';
import { ForkliftInspection } from '../models/forms/forklift-inspection/forklift-inspection.model';
import { EmployeeInjuryReport } from '../models/forms/employee-injury-report/employee-injury-report.model';
import { EnergizedElecWorkPermit } from '../models/forms/energized-elec-work-permit/energized-elec-work-permit.model';
import { SupervisorInjuryReport } from '../models/forms/supervisor-injury-report/supervisor-injury-report.model';
import { JobSafetyAudit } from '../models/forms/job-safety-audit/job-safety-audit.model';
import { VoluntaryRespiratorUse } from '../models/forms/voluntary-respirator/voluntary-respirator.model';
import { TrenchingExcavationWorkPlan } from '../models/forms/trenching-excavation-work-plan/trenching-excavation-work-plan.model';
/**
 * Static mapping of form types to their corresponding model classes
 * Used for compile-time form model access without dependency injection
 * Each entry maps a form type string to its actual model class
 */
export const formTypeModelMap: Record<string, any> = {
  [WeeklySiteInspection.getFormInfo().formType]: WeeklySiteInspection,
  [NearMissReport.getFormInfo().formType]: NearMissReport,
  [HotWorkPermit.getFormInfo().formType]: HotWorkPermit,
  [FallProtectionPlan.getFormInfo().formType]: FallProtectionPlan,
  [PreTaskSafetyPlan.getFormInfo().formType]: PreTaskSafetyPlan,
  [PfizerPreTaskSafetyPlan.getFormInfo().formType]: PfizerPreTaskSafetyPlan,
  [ConfinedSpaceAlternateEntryForm.getFormInfo().formType]: ConfinedSpaceAlternateEntryForm,
  [AerialAndScissorLift.getFormInfo().formType]: AerialAndScissorLift,
  [ForkliftInspection.getFormInfo().formType]: ForkliftInspection,
  [EmployeeInjuryReport.getFormInfo().formType]: EmployeeInjuryReport,
  [SupervisorInjuryReport.getFormInfo().formType]: SupervisorInjuryReport,
  [EnergizedElecWorkPermit.getFormInfo().formType]: EnergizedElecWorkPermit,
  [JobSafetyAudit.getFormInfo().formType]: JobSafetyAudit,
  [VoluntaryRespiratorUse.getFormInfo().formType]: VoluntaryRespiratorUse,
  [TrenchingExcavationWorkPlan.getFormInfo().formType]: TrenchingExcavationWorkPlan,
};

/**
 * Retrieves the model class for a given form type
 * Provides compile-time access to form models without service dependency
 * Used primarily for form rendering and initialization scenarios
 * 
 * @param formType - The form type identifier string
 * @returns The model class for the specified form type, or undefined if not found
 */
export function getFormModel(formType: string): any | undefined {
  return formTypeModelMap[formType];
}

/**
 * Gets the human-readable title for a form type
 * Retrieves the title from the form model's metadata
 * Falls back to a formatted version of the form type if no title exists
 * 
 * @param formType - The form type identifier string
 * @returns The human-readable title for the form type
 */
export function getFormTitle(formType: string): string {
  const modelInfo = getFormModel(formType);
  if (modelInfo?.getFormInfo()?.title) {
    return modelInfo.getFormInfo().title;
  }
  // Fallback to a formatted version of the form type if no title exists
  return formType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Gets the display label for a specific form field
 * Retrieves the field label from the form model's field mappings
 * Falls back to a formatted version of the field key if no mapping exists
 * 
 * @param formType - The form type identifier string
 * @param fieldKey - The field identifier/key
 * @returns The human-readable label for the form field
 */
export function getFormFieldLabel(formType: string, fieldKey: string): string {
  const modelInfo = getFormModel(formType);
  if (modelInfo?.getFieldMappings()[fieldKey]) {
    return modelInfo.getFieldMappings()[fieldKey];
  }
  // Fallback to a formatted version of the key if no mapping exists
  return fieldKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase());
}